import { Loader } from "@googlemaps/js-api-loader";

export type GooglePlacesAddressParsed = {
  line1?: string;
  line2?: string;
  city?: string;
  postalCode?: string;
  state?: string;
  country?: string;
  postalCodeIncomplete: boolean;
  timezone?: string;
};

export const isParsedAddressValid = (
  address: GooglePlacesAddressParsed | null,
): boolean => {
  if (!address) return false;
  return Boolean(
    address.line1 &&
    address.city &&
    address.state &&
    address.postalCode &&
    address.country,
  );
};

const getDefaultTimezone = (country: string): string => {
  switch (country.toUpperCase()) {
    case "AU":
      return "Australia/Sydney";
    case "CA":
      return "America/Toronto";
    case "US":
    default:
      return "America/New_York";
  }
};

export const getTimezoneFromLocation = async (
  apiKey: string,
  lat: number,
  lng: number,
): Promise<string | null> => {
  if (!apiKey) return null;

  try {
    const timestamp = Math.floor(Date.now() / 1000);
    const url = `https://maps.googleapis.com/maps/api/timezone/json?location=${lat},${lng}&timestamp=${timestamp}&key=${apiKey}`;
    const response = await fetch(url);
    const data = await response.json();
    if (data.status === "OK") return data.timeZoneId;
    return null;
  } catch {
    return null;
  }
};

const primaryCityType = "locality";
const primaryStateType = "administrative_area_level_1";

const alternativeCityTypesSet = new Set([
  "postal_town",
  "sublocality_level_1",
  "administrative_area_level_2",
]);

const alternativeStateTypesSet = new Set(["administrative_area_level_2"]);

export interface GooglePlacesConfig {
  apiKey: string;
  timezoneApiKey?: string;
}

export const getGooglePlaces = async (config: GooglePlacesConfig) => {
  const loader = new Loader({
    apiKey: config.apiKey,
    libraries: ["places"],
  });

  const lib = await loader.importLibrary("places");

  const getAutoCompleteSuggestion =
    lib.AutocompleteSuggestion.fetchAutocompleteSuggestions;

  const sessionToken = new lib.AutocompleteSessionToken();

  const getParsedAddress = async (
    placeId: string,
  ): Promise<GooglePlacesAddressParsed | null> => {
    try {
      const placeStub = new lib.Place({ id: placeId });
      const result = await placeStub.fetchFields({
        fields: ["addressComponents", "location"],
      });

      const address = result.place.addressComponents || [];
      if (!address || address.length === 0) return null;

      const parsedAddress: GooglePlacesAddressParsed = {
        postalCodeIncomplete: false,
      };

      const location = result.place.location;

      for (const component of address) {
        if (component.types?.includes("subpremise") && component.longText) {
          parsedAddress.line2 = component.longText;
        } else if (
          component.types?.includes("street_number") &&
          component.longText
        ) {
          if (parsedAddress.line1) {
            parsedAddress.line1 = `${component.longText} ${parsedAddress.line1}`;
          } else {
            parsedAddress.line1 = component.longText;
          }
        } else if (component.types?.includes("route") && component.longText) {
          if (parsedAddress.line1) {
            parsedAddress.line1 = `${parsedAddress.line1} ${component.longText}`;
          } else {
            parsedAddress.line1 = component.longText;
          }
        } else if (
          component.types?.includes(primaryCityType) &&
          component.longText
        ) {
          parsedAddress.city = component.longText;
        } else if (
          !parsedAddress.city &&
          component.types?.some((type) => alternativeCityTypesSet.has(type)) &&
          component.longText
        ) {
          parsedAddress.city = component.longText;
        } else if (
          component.types?.includes(primaryStateType) &&
          component.longText
        ) {
          parsedAddress.state = component.shortText ?? component.longText;
        } else if (
          !parsedAddress.state &&
          component.types?.some((type) => alternativeStateTypesSet.has(type)) &&
          component.longText
        ) {
          parsedAddress.state = component.shortText ?? component.longText;
        } else if (
          component.types?.includes("postal_code") &&
          component.longText
        ) {
          parsedAddress.postalCode = component.longText;
          if (
            component.types.some(
              (t) => t === "postal_code_prefix" || t === "postal_code_suffix",
            )
          ) {
            parsedAddress.postalCodeIncomplete = true;
          }
        } else if (
          component.types?.includes("country") &&
          component.shortText
        ) {
          parsedAddress.country = component.shortText;
        }
      }

      if (
        !parsedAddress.postalCodeIncomplete &&
        parsedAddress.postalCode &&
        parsedAddress.country
      ) {
        if (parsedAddress.country.toLowerCase() === "us") {
          parsedAddress.postalCodeIncomplete = !/^\d{5}(-\d{4})?$/.test(
            parsedAddress.postalCode,
          );
        } else if (parsedAddress.country.toLowerCase() === "ca") {
          parsedAddress.postalCodeIncomplete = !/^[a-z]\d[a-z]\d[a-z]\d$/.test(
            parsedAddress.postalCode.replace(/\s/g, "").toLowerCase(),
          );
        } else if (parsedAddress.country.toLowerCase() === "au") {
          parsedAddress.postalCodeIncomplete = !/^\d{4}$/.test(
            parsedAddress.postalCode,
          );
        }
      }

      if (location && config.timezoneApiKey) {
        const lat = location.lat();
        const lng = location.lng();
        const timezone = await getTimezoneFromLocation(
          config.timezoneApiKey,
          lat,
          lng,
        );
        if (timezone) {
          parsedAddress.timezone = timezone;
        } else if (parsedAddress.country) {
          parsedAddress.timezone = getDefaultTimezone(parsedAddress.country);
        }
      } else if (parsedAddress.country) {
        parsedAddress.timezone = getDefaultTimezone(parsedAddress.country);
      }

      return parsedAddress;
    } catch {
      return null;
    }
  };

  const getSuggestions = async (
    input: string,
    regions: string[] = ["us", "ca", "au"],
  ): Promise<{ text: string; placesId: string }[]> => {
    const resp = await getAutoCompleteSuggestion({
      input: input,
      includedRegionCodes: regions,
      sessionToken: sessionToken,
    });

    return resp.suggestions
      .filter((s) => s.placePrediction)
      .map((s) => ({
        text: s.placePrediction!.text.text,
        placesId: s.placePrediction!.placeId,
      }));
  };

  return { getSuggestions, getParsedAddress };
};
